import { BUKIETOWA_LOCATIONS } from '@/lib/globalFloristSchema';

/**
 * Treść strony /kontakt — wydzielona z pages/kontakt.tsx jako osobny,
 * JSON-serializable config. Logika formularza (walidacja, wysyłka,
 * stan) i map (IntersectionObserver, zgoda na cookies) zostaje w kodzie
 * — tu wydzielona jest wyłącznie statyczna treść redakcyjna.
 */
export interface Store {
  index: string;
  name: string;
  address: string;
  city: string;
  phone: string;
  tel: string;
  map: string;
}

export interface Topic {
  value: string;
  label: string;
}

export interface FaqEntry {
  q: string;
  a: string;
}

export interface SpecialOffer {
  tag: string;
  heading: string;
  text: string;
  cta: string;
  href: string;
}

export interface HoursRow {
  label: string;
  time: string;
}

export const KONTAKT_CONTENT = {
  hero: {
    headingLine1: 'Skontaktuj się',
    headingAccent: 'z nami',
    body: 'Masz pytanie dotyczące zamówienia, szukasz konkretnego bukietu albo chcesz porozmawiać o większej realizacji? Jesteśmy tutaj, żeby pomóc.',
    ctaCallLabel: 'Zadzwoń',
    ctaWriteLabel: 'Napisz do nas',
  },
  stores: {
    eyebrow: 'Nasze kwiaciarnie',
    heading: 'Odwiedź jedną z naszych kwiaciarni',
    callLabel: 'Zadzwoń',
    routeLabel: 'Wyznacz trasę',
    items: BUKIETOWA_LOCATIONS.map((location, index) => ({
      index: String(index + 1).padStart(2, '0'),
      name: index === 0 ? 'Bukietowa' : 'Bursztynowa',
      address: location.address.streetAddress,
      city: location.address.addressLocality,
      phone: location.telephone,
      tel: `tel:+${location.telephone.replace(/\D/g, '')}`,
      map: location.pickupHref,
    })) satisfies Store[],
  },
  hours: {
    eyebrow: 'Godziny otwarcia',
    subLabel: 'Obie kwiaciarnie',
    rows: [
      { label: 'Poniedziałek – Sobota', time: '9:00 – 19:00' },
      { label: 'Niedziela', time: '10:00 – 16:00' },
    ] satisfies HoursRow[],
  },
  map: {
    eyebrow: 'Lokalizacja',
    heading: 'Znajdź nas',
    showMapLabel: 'Pokaż mapę',
    loadingLabel: 'Ładowanie mapy…',
    routeLabel: 'Wyznacz trasę →',
  },
  form: {
    eyebrow: 'Formularz kontaktowy',
    headingLine1: 'Napisz',
    headingLine2: 'do nas',
    body: 'Jeśli wolisz napisać, zostaw wiadomość - odpowiemy tak szybko, jak to możliwe.',
    email: 'kontakt@bukietowa.pl',
    successHeading: 'Wiadomość wysłana',
    successBody: 'Dziękujemy za kontakt. Odpiszemy tak szybko, jak to możliwe.',
    successResetLabel: 'Wyślij kolejną wiadomość',
    namePlaceholder: 'Imię *',
    emailPlaceholder: 'E-mail *',
    subjectPlaceholder: 'Temat wiadomości',
    messagePlaceholder: 'Wiadomość *',
    submitLabel: 'Wyślij wiadomość',
    submitLabelSending: 'Wysyłanie…',
    genericErrorMessage: 'Coś poszło nie tak. Spróbuj ponownie.',
    networkErrorMessage: 'Błąd połączenia. Sprawdź internet i spróbuj ponownie.',
    topics: [
      { value: 'zamowienie', label: 'Pytanie dotyczące zamówienia' },
      { value: 'dostawa', label: 'Dostawa' },
      { value: 'produkt', label: 'Bukiet / produkt' },
      { value: 'slub', label: 'Ślub' },
      { value: 'firmy', label: 'Dla firm' },
      { value: 'specjalne', label: 'Zamówienie specjalne' },
      { value: 'inne', label: 'Inne' },
    ] satisfies Topic[],
  },
  specials: [
    {
      tag: 'Florystyka ślubna',
      heading: 'Planujecie ślub?',
      text: 'Bukiet Panny Młodej, butonierki, dekoracja ceremonii, stołów i sali - możemy przygotować kompleksową kwiatową oprawę Waszego dnia.',
      cta: 'Zobacz ofertę ślubną',
      href: '/slub/',
    },
    {
      tag: 'Kwiaty dla firm',
      heading: 'Szukasz kwiatów dla firmy?',
      text: 'Realizujemy zamówienia dla firm i przygotowujemy indywidualne realizacje dopasowane do potrzeb.',
      cta: 'Zobacz ofertę dla firm',
      href: '/dla-firm/',
    },
    {
      tag: 'Zamówienia specjalne',
      heading: 'Masz nietypowy pomysł?',
      text: 'Nie znalazłeś tego, czego szukasz? Opowiedz nam - przygotujemy indywidualną propozycję, jeśli tylko będziemy mogli ją zrealizować.',
      cta: 'Zapytaj o zamówienie specjalne',
      href: '/zamowienie-specjalne/',
    },
  ] satisfies SpecialOffer[],
  faq: {
    eyebrow: 'Najczęstsze pytania',
    heading: 'Masz pytania?',
    items: [
      { q: 'W jakich godzinach jesteście otwarci?', a: 'Obie kwiaciarnie są otwarte od poniedziałku do soboty w godzinach 9:00–19:00, a w niedzielę w godzinach 10:00–16:00.' },
      { q: 'Czy mogę zamówić kwiaty telefonicznie?', a: 'Tak - chętnie przyjmiemy zamówienie telefoniczne. Zadzwoń do wybranej kwiaciarni, a dopasujemy bukiet do Twoich potrzeb i ustalimy szczegóły dostawy lub odbioru.' },
      { q: 'Czy mogę odebrać zamówienie osobiście?', a: 'Oczywiście. Możesz zamówić kwiaty przez telefon lub sklep internetowy i odebrać je bezpośrednio w jednej z naszych kwiaciarni.' },
      { q: 'Gdzie znajdują się Wasze kwiaciarnie?', a: 'Prowadzimy dwie kwiaciarnie w Lublinie: przy al. Warszawskiej 23/1 oraz przy ul. Bursztynowej 17/4.' },
      { q: 'Jak skontaktować się w sprawie konkretnego zamówienia?', a: 'Najszybciej przez telefon. Możesz również napisać na kontakt@bukietowa.pl lub wypełnić formularz kontaktowy poniżej - odpiszemy tak szybko, jak to możliwe.' },
      { q: 'Czy realizujecie indywidualne zamówienia?', a: 'Tak, chętnie przygotujemy kompozycję dopasowaną do Twoich potrzeb. Opisz nam, czego szukasz - i zobaczymy, co możemy dla Ciebie stworzyć.' },
      { q: 'Czy przygotowujecie kwiaty na ślub?', a: 'Tak, oferujemy kompleksową florystyczną oprawę ślubną - od bukietu Panny Młodej i butonierek po dekorację ceremonii i sali weselnej.' },
      { q: 'Czy obsługujecie firmy?', a: 'Tak, realizujemy zamówienia dla firm - zarówno jednorazowe, jak i cykliczne. Skontaktuj się z nami, a omówimy szczegóły współpracy.' },
    ] satisfies FaqEntry[],
  },
};
